/**
 * Miscellaneous Styles - Swiss Design
 *
 * Toggles, Chips, Transitions, Backdrop, Touch, Contrast, Emphasis, Semantic, Containers, Alerts
 */

// ============================================================================
// TOGGLE STYLES
// ============================================================================

/**
 * Toggle button styles for filter groups
 * Used for mutually exclusive options (all/active/inactive)
 * WCAG AA compliant with min-h-[44px] touch targets
 */
export const SWISS_TOGGLE = {
  /** Active toggle button */
  active: 'bg-gray-900 border-gray-900 text-white border-2',

  /** Inactive toggle button */
  inactive: 'border-gray-200 text-gray-900 hover:bg-gray-50 bg-white border-2',

  /** Base toggle sizing and typography - WCAG AA compliant (44px min height) */
  base: 'min-h-[44px] px-3 text-sm font-medium uppercase tracking-wide transition-colors inline-flex items-center',
} as const;

// ============================================================================
// CHIP STYLES (Stress-Optimized)
// ============================================================================

/**
 * Enhanced chip tags for stress-optimized environments
 * Larger touch targets, clear typography, high contrast
 * Used in high-pressure scenarios like shop product returns
 */
export const SWISS_CHIP_STRESS = {
  /** Base stress-optimized chip - large, readable, accessible */
  base: 'min-h-[72px] px-6 py-4 border-3 border-gray-500 bg-white rounded-lg shadow-sm transition-all duration-200 cursor-pointer',

  /** Hover state - enhanced border and subtle lift */
  hover: 'hover:border-gray-900 hover:shadow-lg hover:scale-[1.02]',

  /** Expanded state - shows quantity selection */
  expanded: 'border-3 border-blue-600 bg-blue-50/30 shadow-xl',

  /** Success state - after return confirmed */
  success: 'border-3 border-emerald-600 bg-emerald-100',

  /** Complete interactive chip */
  interactive: 'min-h-[72px] px-6 py-4 border-3 border-gray-500 bg-white rounded-lg shadow-sm transition-all duration-200 cursor-pointer hover:border-gray-900 hover:shadow-lg hover:scale-[1.02]',
} as const;

// ============================================================================
// TRANSITIONS
// ============================================================================

/**
 * Standard transition durations and easings
 */
export const SWISS_TRANSITIONS = {
  /** Fast transition - 150ms */
  fast: 'transition-all duration-150 ease-in-out',

  /** Standard transition - 200ms */
  standard: 'transition-all duration-200 ease-in-out',

  /** Slow transition - 300ms */
  slow: 'transition-all duration-300 ease-in-out',

  /** Transform only */
  transform: 'transition-transform duration-200 ease-in-out',

  /** Colors only */
  colors: 'transition-colors duration-200 ease-in-out',

  /** Opacity only */
  opacity: 'transition-opacity duration-200 ease-in-out',
} as const;

// ============================================================================
// BACKDROP EFFECTS
// ============================================================================

/**
 * Backdrop blur effects for glass morphism
 */
export const SWISS_BACKDROP = {
  /** Standard blur */
  blur: 'backdrop-blur-xl',

  /** Light blur */
  blurLight: 'backdrop-blur-lg',

  /** Heavy blur */
  blurHeavy: 'backdrop-blur-2xl',

  /** Blur with background */
  blurBg: 'bg-white/98 backdrop-blur-xl',
} as const;

// ============================================================================
// TOUCH TARGETS
// ============================================================================

/**
 * Touch target sizes for accessibility and stress environments
 * Optimized for high-stress scenarios where accuracy and speed matter
 */
export const SWISS_TOUCH = {
  /** Minimum accessible touch target - 44x44px (WCAG AA) */
  min: 'min-h-[44px] min-w-[44px]',

  /** Comfortable touch target - 56x56px (stress-optimized) */
  comfortable: 'min-h-[56px] min-w-[56px]',

  /** Large touch target - 64x64px (critical actions) */
  large: 'min-h-[64px] min-w-[64px]',

  /** Extra large squared touch target - 88x88px (stress-optimized buttons) */
  largeSquare: 'min-h-[88px] min-w-[88px]',
} as const;

/**
 * Touch target helpers for mobile
 */
export const SWISS_TOUCH_MOBILE = {
  /** Minimum WCAG (44x44) */
  min: 'min-h-[44px] min-w-[44px]',

  /** Preferred for important actions (48x48) */
  comfortable: 'min-h-[48px] min-w-[48px]',

  /** Extended tap area (for small elements) */
  hitArea: 'relative before:absolute before:inset-[-8px] before:content-[""]',

  /** Tap highlight */
  tapHighlight: 'active:bg-gray-100',

  /** No tap highlight (for custom styling) */
  noTapHighlight: '[-webkit-tap-highlight-color:transparent]',
} as const;

// ============================================================================
// CONTRAST SYSTEM (WCAG AA Compliance)
// ============================================================================

/**
 * Contrast ratios for accessibility
 * All combinations meet WCAG AA standards (4.5:1 for text)
 */
export const SWISS_CONTRAST = {
  /** High contrast text on light background */
  textHigh: 'text-gray-900',

  /** High contrast text on dark background */
  textHighInverse: 'text-white',

  /** Enhanced border for stress environments */
  borderStress: 'border-2 border-gray-900',

  /** High contrast background for critical elements */
  bgStress: 'bg-gray-900 text-white',
} as const;

// ============================================================================
// EMPHASIS SYSTEM (Functional Colors with Gradients)
// ============================================================================

/**
 * Emphasis states with functional colors and subtle gradients
 * Used for alerts, notifications, and critical UI states
 */
export const SWISS_EMPHASIS = {
  /** Critical alert - red with gradient */
  critical: 'bg-gradient-to-br from-red-600 to-red-700 text-white border-2 border-red-900/60',

  /** Warning alert - amber with gradient */
  warning: 'bg-gradient-to-br from-amber-500 to-amber-600 text-white border-2 border-amber-700/60',

  /** Success emphasis - emerald with gradient */
  success: 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-2 border-emerald-900/60',

  /** Info emphasis - blue with gradient */
  info: 'bg-gradient-to-br from-blue-600 to-blue-700 text-white border-2 border-blue-900/60',

  /** Action primary - green for positive actions */
  actionPrimary: 'bg-gradient-to-br from-green-600 to-green-700 text-white border-2 border-green-900/60',

  /** Action secondary - blue for neutral actions */
  actionSecondary: 'bg-gradient-to-br from-blue-600 to-blue-700 text-white border-2 border-blue-900/60',

  /** Action attention - orange for time-sensitive */
  actionAttention: 'bg-gradient-to-br from-orange-600 to-orange-700 text-white border-2 border-orange-900/60',
} as const;

// ============================================================================
// SEMANTIC COLORS (Subtle Background States)
// ============================================================================

/**
 * Semantic color system for UI states
 * Subtle backgrounds with colored text for non-critical feedback
 */
export const SWISS_SEMANTIC = {
  /** Error state background */
  error: 'text-red-700 bg-red-50/80 border-l-4 border-red-600',

  /** Warning state background */
  warning: 'text-amber-700 bg-amber-50/80 border-l-4 border-amber-600',

  /** Success state background */
  success: 'text-emerald-700 bg-emerald-50/80 border-l-4 border-emerald-600',

  /** Info state background */
  info: 'text-blue-700 bg-blue-50/80 border-l-4 border-blue-600',

  /** Neutral state background */
  neutral: 'text-gray-700 bg-gray-50/80 border-l-4 border-gray-400',
} as const;

// ============================================================================
// CONTAINER SYSTEM
// ============================================================================

/**
 * Container and spacing patterns
 * Consistent layout structure across pages
 */
export const SWISS_CONTAINER = {
  /** Page container with responsive padding */
  page: 'container mx-auto px-4',

  /** Section spacing between major blocks */
  section: 'mb-8',

  /** Standard card padding */
  cardPadding: 'p-6',

  /** Stress-optimized card padding (50% more) */
  cardPaddingStress: 'p-8',

  /** Modal content padding */
  modalPadding: 'p-8',

  /** Compact padding for tight spaces */
  compact: 'p-4',
} as const;

/**
 * Page and section containers for mobile
 */
export const SWISS_CONTAINER_MOBILE = {
  /** Page container - minimal padding */
  page: 'px-3 pb-3',

  /** Safe area (for notch/home indicator) */
  safeArea: 'px-3 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]',

  /** Section */
  section: 'mb-4',

  /** Card padding */
  cardPadding: 'p-3',

  /** Compact padding */
  compact: 'p-2',
} as const;

// ============================================================================
// ALERT SYSTEM (Mobile)
// ============================================================================

/**
 * Inline alerts for mobile (no toast)
 */
export const SWISS_ALERT_MOBILE = {
  /** Base inline alert */
  base: 'px-3 py-2 rounded-lg border-l-4 text-sm',

  /** Success */
  success: 'bg-emerald-50 border-emerald-500 text-emerald-800',

  /** Warning */
  warning: 'bg-amber-50 border-amber-500 text-amber-800',

  /** Error */
  error: 'bg-rose-50 border-rose-500 text-rose-800',

  /** Info */
  info: 'bg-blue-50 border-blue-500 text-blue-800',

  /** Compact banner (top of page) */
  banner: 'px-3 py-2 text-sm font-medium text-center',
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type SwissToggle = keyof typeof SWISS_TOGGLE;
export type SwissChipStress = keyof typeof SWISS_CHIP_STRESS;
export type SwissTransition = keyof typeof SWISS_TRANSITIONS;
export type SwissBackdrop = keyof typeof SWISS_BACKDROP;
export type SwissTouch = keyof typeof SWISS_TOUCH;
export type SwissTouchMobile = keyof typeof SWISS_TOUCH_MOBILE;
export type SwissContrast = keyof typeof SWISS_CONTRAST;
export type SwissEmphasisType = keyof typeof SWISS_EMPHASIS;
export type SwissSemantic = keyof typeof SWISS_SEMANTIC;
export type SwissContainer = keyof typeof SWISS_CONTAINER;
export type SwissContainerMobile = keyof typeof SWISS_CONTAINER_MOBILE;
export type SwissAlertMobile = keyof typeof SWISS_ALERT_MOBILE;
